import {
  Box,
  Button,
  Flex,
  Heading,
  Stack,
  Text,
  Icon,
  VStack,
  Image,
  useBreakpointValue,
  Link,
  SimpleGrid,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useEffect } from "react";
import SEO from "../SEO";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { FaLightbulb } from "react-icons/fa";

const MotionBox = motion(Box);
const MotionHeading = motion(Heading);
const MotionText = motion(Text);
const MotionLink = motion(Link);

const WobbleStartLanding = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 4000,
  };

  const isMobile = useBreakpointValue({ base: true, md: false });

  return (
    <>
      <SEO
        title="Wobble Start | Online időpontfoglalás induló szolgáltatóknak"
        description="Nézd meg élőben, hogyan működne egy online időpontfoglaló rendszer a te vállalkozásod számára. A Wobble Start bemutató segít abban, hogy magabiztosan elindulj digitálisan."
        keywords="időpontfoglalás, online naptár, digitális jelenlét, induló szolgáltatás, szolgáltatók, online rendszer, vállalkozás indítás"
        ogTitle="Wobble Start | Online időpontfoglalás induló szolgáltatóknak"
        ogDescription="Próbáld ki élőben, milyen lenne egy saját időpontfoglaló rendszered. Nincs nyomulás, csak konkrét válaszok és működő példák."
        ogImage="https://wobble.hu/assets/mockup_massage.png"
      />

      {/* Hero Section with Slider */}
      <Box
        as="section"
        minH="100vh"
        bg="gray.50"
        px={{ base: 4, md: 8 }}
        py={{ base: 16, md: 32 }}
      >
        <Flex
          direction="column"
          justify="center"
          align="center"
          maxW="6xl"
          mx="auto"
          gap={10}
        >
          <MotionHeading
            as="h1"
            fontSize={{ base: "2xl", md: "4xl" }}
            fontWeight="bold"
            color="gray.800"
            textAlign="center"
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Wobble Start 🎯 - Induló szolgáltatóknak
          </MotionHeading>

          <MotionBox
            flex="1"
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Text fontSize="lg" color="gray.700">
              Ingyenes online bemutató - időpontfoglaló rendszer és online
              jelenlét
            </Text>
          </MotionBox>

          <MotionLink
            href="https://book.wobble.hu/"
            isExternal
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button colorScheme="teal" size="lg">
              Wobble Start 🎯
            </Button>
          </MotionLink>

          <Box w="full" maxW={isMobile ? "100%" : 500} mt={10}>
            <Slider {...sliderSettings}>
              <Image
                src="/assets/mockup_workshop.png"
                alt="Slide 1"
                borderRadius="xl"
              />
              <Image
                src="/assets/offer/mockup_calendar_website.png"
                alt="Slide 2"
                borderRadius="xl"
              />
              <Image
                src="/assets/mockup_massage.png"
                alt="Slide 3"
                borderRadius="xl"
              />
               <Image
                src="/assets/mockup_moov_studio.png"
                alt="Slide 3"
                borderRadius="xl"
              />
               <Image
                src="/assets/mockup_riding_school.png"
                alt="Slide 3"
                borderRadius="xl"
              />
               <Image
                src="/assets/mockup_dog_grooming.png"
                alt="Slide 3"
                borderRadius="xl"
              />
            </Slider>
          </Box>
        </Flex>
      </Box>

      {/* Section 2: Objections */}
      <Box
        as="section"
        bg="white"
        px={{ base: 4, md: 8 }}
        py={{ base: 20, md: 28 }}
      >
        <Flex
          direction={{ base: "column", md: "row" }}
          maxW="6xl"
          mx="auto"
          align="center"
          gap={10}
        >
          <MotionBox
            flex="1"
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Image
              src="/assets/wobble_think.png"
              alt="Client questions"
              borderRadius="xl"
              objectFit="cover"
            />
          </MotionBox>

          <MotionBox
            flex="1"
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Heading size="lg" mb={4} color="gray.800">
              Ha benned is felmerült már, hogy...
            </Heading>
            <VStack align="start" spacing={3}>
              {[
                "💬 „Jó lenne valami rendszer, de nem tudom, nekem való-e…”",
                "💬 „Nézegettem már párat, de túl bonyolultnak tűnt…”",
                "💬 „Lehet, hogy felesleges? Vagy pont ez hiányzik?”",
                "💬 „Nem akarok előfizetni csak úgy, de érdekelne, hogyan működne nálam.”",
                "💬 „A konkurensem már használ valamit, de nem tudom, hogyan kezdte.”",
              ].map((text, idx) => (
                <Text key={idx} fontSize="md" color="gray.700">
                  {text}
                </Text>
              ))}
            </VStack>
            <Text
              fontSize={{ base: "lg", md: "xl" }}
              fontWeight="semibold"
              color="gray.700"
              mb={4}
              mt={8}
            >
              Ismerős gondolatok?
            </Text>
          </MotionBox>
        </Flex>
      </Box>

      <Box>
        <MotionBox
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          py={{ base: 16, md: 24 }}
          px={{ base: 6, md: 12 }}
          bg="gray.50"
        >
          <Box maxW="5xl" mx="auto" textAlign="center">
            <Flex
              direction={{ base: "column", md: "row" }}
              align="center"
              justify="center"
              gap={8}
            >
              <Box>
                <Icon as={FaLightbulb} w={16} h={16} color="teal.400" mb={4} />
              </Box>
              <Box maxW="2xl">
                <Text
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="semibold"
                  color="gray.700"
                  mb={8}
                  mt={8}
                >
                  Ez a bemutató Neked szól, ha
                </Text>

                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="gray.600"
                  mb={8}
                >
                  ... már eljátszottál a gondolattal, hogy jó lenne egy egyszerű
                  rendszer, de még nem tudod pontosan mit tud.
                </Text>
                <Text fontSize={{ base: "md", md: "lg" }} color="gray.600" mb={8}>
                  ... kíváncsi vagy, hogyan működik, mit adhat Neked, hogy tud
                  segíteni időt és pénzt spórolni.
                </Text>
                <Text fontSize={{ base: "md", md: "lg" }} color="gray.600" mb={8}>
                  ... szeretnél jobb online jelenlétet, de nem tudod merre
                  indulj, mert sok idő megtanulni mindent.
                </Text>
                <Text fontSize={{ base: "md", md: "lg" }} color="gray.600" mb={8}>
                  ... ha kérdésed van, de félsz kérdezni mások előtt.
                </Text>
              </Box>
            </Flex>
          </Box>
        </MotionBox>
      </Box>

      {/* Section 3: Problem - Empty Calendar */}
      <Box
        as="section"
        bgGradient="linear(to-b, blue.900, blue.600)"
        color="white"
        py={{ base: 20, md: 32 }}
        px={6}
      >
        <Flex
          direction="column"
          maxW="4xl"
          mx="auto"
          textAlign="center"
          gap={6}
        >
          <MotionHeading
            size="xl"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            Elindultál. Megvan a név, a logó, a szolgáltatás. Álmodoztál róla,
            terveztél, készültél.
          </MotionHeading>
          {[
            "De aztán... csend. A naptár üres.",
            "Jön egy érdeklődő, de mire válaszolnál, már máshol foglalt.",
            "Pedig csak annyi kellett volna: egy rendszer, ami láthatóvá tesz, és hagyja, hogy az ügyfelek maguktól találjanak rád - és foglaljanak.",
          ].map((text, idx) => (
            <MotionText
              key={idx}
              fontSize="lg"
              color="gray.200"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: idx * 0.2 }}
            >
              {text}
            </MotionText>
          ))}
          <MotionText
            fontSize="lg"
            color="gray.200"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 4 * 0.2 }}
          >
            <Text fontSize="xl" color="white" lineHeight="tall">
              A{" "}
              <Text as="span" fontWeight="bold" color="teal.500">
                Wobble Start
              </Text>{" "}
              ingyenes bemutatón megmutatjuk, hogyan lesz az online jelenléted
              meggyőző, és az időpontkezelés a napod legsimább része.
            </Text>
          </MotionText>
          <MotionText
            fontSize="lg"
            color="gray.200"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 5 * 0.2 }}
          >
            <Text fontSize="xl" color="white" lineHeight="tall">
              👉 Ez a konzultáció csak rólad szól. Nincs tömeg, nincs zavaró
              kérdés - kizárólag rád figyelünk. Teljes figyelmet kapsz, hogy a
              saját igényeidre szabott megoldás szülessen.{" "}
            </Text>
          </MotionText>
          <MotionText
            fontSize="lg"
            color="gray.200"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 5 * 0.2 }}
          >
            <Text fontSize="xl" color="white" lineHeight="tall">
              📌 Mit kell tenned? Csak foglalj egy Wobble start 🎯 időpontot,
              gyere el, kérdezz, nézd meg a bemutatót, tanulj. Semmi
              elköteleződés, semmi kártya adat. Tedd meg az első lépést a
              digitális tudás megszerzéséért.
            </Text>
          </MotionText>
          <MotionLink
            href="https://book.wobble.hu/"
            isExternal
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button colorScheme="teal" size="lg">
              Wobble Start 🎯
            </Button>
          </MotionLink>
        </Flex>
      </Box>

      <Box as="section" bg="gray.100" py={{ base: 20, md: 28 }} px={6}>
        <Stack maxW="5xl" mx="auto" spacing={10}>
          <Heading size="lg" textAlign="center" color="gray.800">
            Mit kapsz pontosan a bemutatón?
          </Heading>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            {[
              "Élő rendszerbemutató csak Neked - mire jó egy ilyen rendszer és hogyan lehet hatékonyan használni",
              "Amit láthatsz: az alap beállítások, szolgáltatás létrehozások, időpontok beállítása, várólista, speciális opciok, weboldal beállítás, Google naptár integráció, blog, email",
              "Válaszok a kérdéseidre, kételyeidre",
            ].map((item, idx) => (
              <MotionBox
                key={idx}
                p={6}
                bg="white"
                borderRadius="2xl"
                boxShadow="2xl"
                textAlign="center"
                whileHover={{ y: -4 }}
                transition={{ duration: 0.3 }}
              >
                <Text fontSize="2xl" mb={4}>
                  🚀
                </Text>
                <Text fontWeight="semibold" color="gray.700">
                  {item}
                </Text>
              </MotionBox>
            ))}
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
            {[
              "Konkrét példák, hogyan illeszkedne a te szolgáltatásodhoz",
              "Segítség abban, hogyan indulj el digitálisan profin - anélkül, hogy elvesznél a technikai részletekben",
            ].map((item, idx) => (
              <MotionBox
                key={idx + 3}
                p={6}
                bg="white"
                borderRadius="2xl"
                boxShadow="2xl"
                textAlign="center"
                whileHover={{ y: -4 }}
                transition={{ duration: 0.3 }}
              >
                <Text fontSize="2xl" mb={4}>
                  🚀
                </Text>
                <Text fontWeight="semibold" color="gray.700">
                  {item}
                </Text>
              </MotionBox>
            ))}
          </SimpleGrid>
        </Stack>
      </Box>

      <Box py={{ base: 10, md: 20 }} px={{ base: 6, md: 12 }}>
        <Flex
          direction={{ base: "column", md: "row" }}
          maxW="6xl"
          mx="auto"
          align="center"
          gap={10}
        >
          <MotionBox
            flex="1"
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <VStack align="start" spacing={4}>
              {[
                "Képzeld el, milyen érzés, amikor minden gördülékenyen működik.",
                "Egy személyre szabott rendszer, ami leveszi a válladról a szervezés terhét, miközben te csak a munkádra koncentrálsz. Megkönnyebbülés, elégedettség – ez vár rád, amikor a megfelelő eszköz van a kezedben.",
              ].map((text, idx) => (
                <Text key={idx} fontSize="md" color="gray.700">
                  {text}
                </Text>
              ))}
            </VStack>
            <Text
              fontSize={{ base: "lg", md: "xl" }}
              fontWeight="semibold"
              color="gray.700"
              mt={8}
            >
              Megér 40 percet, nem?
            </Text>
          </MotionBox>

          <MotionBox
            flex="1"
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <Image
              src="/assets/wobble_coach.png"
              alt="Wobble coach"
              borderRadius="xl"
              objectFit="cover"
              w="100%"
              h="auto"
            />
          </MotionBox>
        </Flex>
      </Box>

      {/* Section 5: Final CTA */}
      <Box as="section" bg="white" py={24} px={6} textAlign="center">
        <Stack spacing={6} maxW="3xl" mx="auto">
          <Heading size="lg" color="gray.800">
            Ne maradj le - foglald le most az időpontod a Wobble Start
            bemutatóra!
          </Heading>
          <Text fontSize="md" color="gray.700">
            Az ingyenes bemutató végén tisztán fogod látni, mennyit számít egy
            működő rendszer. Nincs nyomulás, csak hasznos válaszok.
          </Text>
          <Text fontSize="md" color="gray.700">
            Valamint 30 napig ingyen tudod tesztelni a rendszert elköteleződés
            nélkül.
          </Text>
          <MotionLink
            href="https://book.wobble.hu/"
            isExternal
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button colorScheme="teal" size="lg">
              Wobble Start 🎯
            </Button>
          </MotionLink>
        </Stack>
      </Box>
    </>
  );
};

export default WobbleStartLanding;
